import type { InsuranceStatus } from "@frontend/lib/actions/actions";
import type { FormInsuranceData } from "@frontend/lib/schemas/clientSchema";
import type { StateCreator } from "zustand";
import type { Store } from "../globalStore";

/**
 * @FIXME: @FIXME We are using firstname / fname interchangeably
 */
export interface Consumer {
  id: string;
  firstname?: string;
  lastname?: string;
  fname: string;
  lname: string;
  dob: string;
  sex?:
    | {
        id: number;
        label: string;
      }
    | undefined;
  streetAddress?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
  zip?: string | null | undefined;
  country?: string | null | undefined;
  ehrPatientId?: string | null | undefined;
  ehrEnterpriseId?: string | null | undefined;
  stripeCustomerId?: string | null | undefined;
}

export interface ConsumerSlice {
  consumer: Consumer;
  /* Because consumer edit takes place over several screens,
   * we store an 'in progress' consumer / insurance to flush all at once.
   */
  inProgressConsumer: Consumer;
  /* Because the in progress consumer might not be the current consumer,
   * this is a cache of their data from before we started editing.
   */
  oldInProgressConsumer: Consumer;
  inProgressInsurances: FormInsuranceData[];
  insurances: FormInsuranceData[];
  /* Stores the full results from the last insurance check API call,
   * including both successful and failed insurance verifications.
   * Used to display contextual error messages.
   */
  lastInsuranceCheckResults: InsuranceStatus[] | null;
  setConsumer: (newValue: Consumer) => void;
  setOldInProgressConsumer: (newValue: Consumer) => void;
  setInProgressConsumer: (newValue: Consumer) => void;
  setInProgressInsurances: (newValue: FormInsuranceData[]) => void;
  setInsurances: (insurances: FormInsuranceData[]) => void;
  setLastInsuranceCheckResults: (results: InsuranceStatus[] | null) => void;
}

export const defaultConsumer = {
  id: "",
  fname: "",
  lname: "",
  dob: "",
};

export const consumerInitialState = {
  consumer: defaultConsumer,
  inProgressConsumer: defaultConsumer,
  oldInProgressConsumer: defaultConsumer,
  inProgressInsurances: [],
  insurances: [],
  lastInsuranceCheckResults: null,
};

export const createConsumerSlice: StateCreator<
  Store,
  [["zustand/devtools", never], ["zustand/persist", unknown]],
  [],
  ConsumerSlice
> = (set, get) => ({
  ...consumerInitialState,
  setConsumer: (newValue) => {
    set({ consumer: newValue });
  },
  setOldInProgressConsumer: (oldInProgressConsumer) =>
    set({ oldInProgressConsumer }),
  setInProgressConsumer: (inProgressConsumer) => set({ inProgressConsumer }),
  setInProgressInsurances: (inProgressInsurances) =>
    set({ inProgressInsurances }),
  setInsurances: (insurances) => {
    set({ insurances });
    // always set this to false again directly as we redo validation regardless
    set({ isInsuranceValidated: false });
  },
  setLastInsuranceCheckResults: (results) => {
    set({ lastInsuranceCheckResults: results });
  },
});
